import React, { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { InstallPrompt } from "./components/InstallPrompt";
import { AuthProvider } from "./context/AuthContext";
import { AudioProvider } from "./context/AudioContext";
import { BackgroundMusicProvider } from "./context/BackgroundMusicContext";
import { LiveCallsProvider } from "./context/LiveCallsContext";
import { ThemeProvider } from "./context/ThemeContext";
import { PaywallProvider } from "./context/PaywallContext";
import CallOverlay from "./components/CallOverlay";
import Layout, { useIsImmersiveRoute } from "./components/Layout";
import I18nLanguageSync from "./components/I18nLanguageSync";
import PrefsSync from "./components/PrefsSync";
import ScreenAwake from "./components/ScreenAwake";
import { WorshipRadioProvider } from "./context/WorshipRadioContext";
import WorshipRadioBar from "./components/WorshipRadioBar";
import { GospelRadioProvider } from "./context/GospelRadioContext";
import GospelRadioBar from "./components/GospelRadioBar";
import GospelRadioSheet from "./components/GospelRadioSheet";
import { installRoutePrefetch } from "./lib/routePrefetch";
// ─── Eager routes (loaded on first paint) ────────────────────────────
// Only Home + the unauthenticated entry-points are eager so the first-
// paint bundle stays small. Every other page is lazy-loaded the moment
// the user navigates to it. This dropped the initial JS download by
// roughly 60-80% in measurements; on a cold 4G connection that means
// Home becomes interactive in roughly half the time it did before.
import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AuthCallback from "./pages/AuthCallback";
// ─── Lazy routes (loaded on demand) ──────────────────────────────────
const BiblePage           = lazy(() => import("./pages/Bible"));
const Reader              = lazy(() => import("./pages/Reader"));
const Videos              = lazy(() => import("./pages/Videos"));
const Movies              = lazy(() => import("./pages/Movies"));
const MoviesBlackAmerican = lazy(() => import("./pages/MoviesBlackAmerican"));
const Kids                = lazy(() => import("./pages/Kids"));
const Churches            = lazy(() => import("./pages/Churches"));
const AdminChurches       = lazy(() => import("./pages/AdminChurches"));
const AdminCloudinary     = lazy(() => import("./pages/AdminCloudinary"));
const AdminVoiceMisses    = lazy(() => import("./pages/AdminVoiceMisses"));
const AdminMusic          = lazy(() => import("./pages/AdminMusic"));
const AdminReviewQueue    = lazy(() => import("./pages/AdminReviewQueue"));
const Storybook           = lazy(() => import("./pages/Storybook"));
const Discover            = lazy(() => import("./pages/Discover"));
const DiscoverShorts      = lazy(() => import("./pages/DiscoverShorts"));
const ForYou              = lazy(() => import("./pages/ForYou"));
const Playlists           = lazy(() => import("./pages/Playlists"));
const LivePage            = lazy(() => import("./pages/LivePage"));
const Wall                = lazy(() => import("./pages/Wall"));
const Memorial            = lazy(() => import("./pages/Memorial"));
const LiveViewer          = lazy(() => import("./pages/LiveViewer"));
const Search              = lazy(() => import("./pages/Search"));
const Bookmarks           = lazy(() => import("./pages/Bookmarks"));
const History             = lazy(() => import("./pages/History"));
const MyNotes             = lazy(() => import("./pages/MyNotes"));
const CreatorFeed         = lazy(() => import("./pages/CreatorFeed"));
const ChannelWatch        = lazy(() => import("./pages/ChannelWatch"));
const Sermons             = lazy(() => import("./pages/Sermons"));
const News                = lazy(() => import("./pages/News"));
const Lessons             = lazy(() => import("./pages/Lessons"));
const Choir               = lazy(() => import("./pages/Choir"));
const GospelMusic         = lazy(() => import("./pages/GospelMusic"));
const MusicVideos         = lazy(() => import("./pages/MusicVideos"));
const Comedy              = lazy(() => import("./pages/Comedy"));
const FoundationalComedy  = lazy(() => import("./pages/FoundationalComedy"));
const LogoPreview         = lazy(() => import("./pages/LogoPreview"));
const ChristianRap        = lazy(() => import("./pages/ChristianRap"));
const Demo                = lazy(() => import("./pages/Demo"));
const DemoReel            = lazy(() => import("./pages/DemoReel"));
const MyMix               = lazy(() => import("./pages/MyMix"));
const CommunityShorts     = lazy(() => import("./pages/CommunityShorts"));
const UploadShort         = lazy(() => import("./pages/UploadShort"));
const AdminAnalytics      = lazy(() => import("./pages/AdminAnalytics"));
const PrayerWall          = lazy(() => import("./pages/PrayerWall"));
const Apocrypha           = lazy(() => import("./pages/Apocrypha"));
const ApocryphaReader     = lazy(() => import("./pages/ApocryphaReader"));
const Dictionary          = lazy(() => import("./pages/Dictionary"));
const Concordance         = lazy(() => import("./pages/Concordance"));
const Crossword           = lazy(() => import("./pages/Crossword"));
const Precepts            = lazy(() => import("./pages/Precepts"));
const StudyGroup          = lazy(() => import("./pages/StudyGroup"));
const StudyRoomLive        = lazy(() => import("./pages/StudyRoomLive"));
const StudyChatEntry       = lazy(() => import("./pages/StudyChatEntry"));
const Read                = lazy(() => import("./pages/Read"));
const Rooms               = lazy(() => import("./pages/Rooms"));
const Room                = lazy(() => import("./pages/Room"));
const InviteAccept        = lazy(() => import("./pages/InviteAccept"));
const Settings            = lazy(() => import("./pages/Settings"));
const CastGuide           = lazy(() => import("./pages/CastGuide"));
const ForgotPassword      = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword       = lazy(() => import("./pages/ResetPassword"));
const Privacy             = lazy(() => import("./pages/Privacy"));
const ChildSafety         = lazy(() => import("./pages/ChildSafety"));
const Terms               = lazy(() => import("./pages/Terms"));
const SmsTerms            = lazy(() => import("./pages/SmsTerms"));
const AcceptableUse       = lazy(() => import("./pages/AcceptableUse"));
const About               = lazy(() => import("./pages/About"));
const Articles            = lazy(() => import("./pages/Articles"));
const ArticleDetail       = lazy(() => import("./pages/ArticleDetail"));
const Faq                 = lazy(() => import("./pages/Faq"));
const Tour                = lazy(() => import("./pages/Tour"));
const MyPrayers           = lazy(() => import("./pages/MyPrayers"));
const HolyLetters         = lazy(() => import("./pages/HolyLetters"));
const NotFound            = lazy(() => import("./pages/NotFound"));
const SundaySchool        = lazy(() => import("./pages/SundaySchool"));
const Recipes             = lazy(() => import("./pages/Recipes"));
const RecipeDetail        = lazy(() => import("./pages/RecipeDetail"));
const MyProfile           = lazy(() => import("./pages/MyProfile"));
const CreatorStudio       = lazy(() => import("./pages/CreatorStudio"));
const UserProfile         = lazy(() => import("./pages/UserProfile"));
const Friends             = lazy(() => import("./pages/Friends"));
const Circles             = lazy(() => import("./pages/Circles"));
const CircleView          = lazy(() => import("./pages/CircleView"));
const Messages            = lazy(() => import("./pages/Messages"));
const MessageThread       = lazy(() => import("./pages/MessageThread"));
const Support             = lazy(() => import("./pages/Support"));
const InviteFriends        = lazy(() => import("./pages/InviteFriends"));
const Diagnostics         = lazy(() => import("./pages/Diagnostics"));
const MyLineup            = lazy(() => import("./pages/MyLineup"));
const SubscribersWatch    = lazy(() => import("./pages/SubscribersWatch"));
const DemoLogin           = lazy(() => import("./pages/DemoLogin"));
const ChannelBySlug       = lazy(() => import("./pages/ChannelBySlug"));
import VoiceCommander from "./components/VoiceCommander";
import QuickShortcutsRail from "./components/QuickShortcutsRail";
import MiniPlayer from "./components/MiniPlayer";
import LineupCountBar from "./components/LineupCountBar";
import PushOptInBanner from "./components/PushOptInBanner";
import AddToHomeTip from "./components/AddToHomeTip";
import { AdSenseScript } from "./components/AdSense";
// SiteFooter is now mounted exclusively from Home.jsx (after the
// "Animated stories" / Tower of Babel grid) per the founder's
// request — other routes intentionally have no footer card.
import { MiniPlayerProvider } from "./context/MiniPlayerContext";
import { CaptionsProvider } from "./context/CaptionsContext";
import { VoiceRoomProvider } from "./context/VoiceRoomContext";
import LiveCaptions from "./components/LiveCaptions";
import { initAnalytics, trackPageview } from "./lib/analytics";

// Inline lightweight fallback so React.Suspense doesn't flash a blank
// page while the page-chunk downloads. Branded with the same forest
// accent the rest of the app uses. Fills the entire viewport with a
// solid cream background so the SiteFooter (rendered below the
// Suspense boundary) doesn't peek through during route transitions —
// users were seeing the footer + bio tagline as a "ghost view" while
// switching genres. The solid cream cover guarantees a clean fade.
function RouteLoading() {
  return (
    <div
      className="fixed inset-0 z-[5] bg-cream grid place-items-center"
      data-testid="route-loading"
    >
      <div className="flex items-center gap-3 text-muted_ink text-sm">
        <span className="inline-block w-3 h-3 rounded-full bg-forest animate-pulse" />
        <span className="tracking-wider uppercase text-[11px]">Loading…</span>
      </div>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  const immersive = useIsImmersiveRoute();
  // Google Analytics 4 — initialized once on app boot (no-op when the
  // `REACT_APP_GA_MEASUREMENT_ID` env var is empty), then a pageview
  // is fired on every React Router location change. Without the
  // per-location effect, GA would only ever record the very first
  // page load because the SPA never triggers a real document
  // navigation between routes.
  useEffect(() => { initAnalytics(); }, []);
  // Warm the next page's lazy chunk on link hover/touch so navigating
  // between routes feels near-instant (pairs with the new skeletons).
  useEffect(() => installRoutePrefetch(), []);
  // Capture ?ref=CODE referral parameter on app boot. Stash in
  // localStorage so AuthContext can redeem it as soon as the user
  // signs in (either via this session or a future one). We don't
  // call /invite/redeem here because the user may not yet be auth'd.
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const ref = url.searchParams.get("ref");
      if (ref && /^[a-z0-9]{6,32}$/i.test(ref)) {
        window.localStorage.setItem("hw_pending_ref", ref.toLowerCase());
      }
    } catch (_) {}
  }, []);
  useEffect(() => {
    trackPageview(location.pathname + location.search);
    // Scroll to the top of the page on every route change so users
    // don't briefly see the previous page's footer while the new
    // route mounts. Without this, navigating from a long page that
    // was scrolled to the bottom (e.g. /movies) over to /home left
    // the user looking at the site footer for a beat until they
    // manually scrolled up. Using `instant` instead of `smooth` so
    // the jump is invisible — feels like a fresh page each time.
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    } catch (_) {
      window.scrollTo(0, 0);
    }
    // SEO — keep the <link rel="canonical"> in sync with the current
    // route. Without this, the hardcoded canonical in index.html
    // (which points to `/`) makes Google Search Console treat every
    // sub-page (/sermons, /bible, /shorts, /movies/black-american,
    // /comedy, etc.) as an "Alternate page with proper canonical
    // tag" — i.e. a duplicate of the homepage — and they never get
    // indexed individually. Updating the canonical to the current
    // path on every navigation gives each route its own indexable
    // URL while keeping the homepage canonical at "/". Query strings
    // and hash fragments are stripped (they're never canonical).
    try {
      const link = document.querySelector('link[rel="canonical"]');
      if (link) {
        const origin = window.location.origin || "https://holywords144.com";
        // Trim trailing slash on non-root paths so /sermons and
        // /sermons/ both canonicalize to /sermons.
        const path = location.pathname === "/" ? "/" : location.pathname.replace(/\/+$/, "");
        link.setAttribute("href", `${origin}${path}`);
      }
    } catch (_) { /* noop */ }
  }, [location.pathname, location.search]);

  // Synchronously detect Google OAuth callback BEFORE rendering normal routes.
  // We check BOTH the react-router location.hash AND the live
  // window.location.hash, then OR them together. The window check
  // covers the brief moment after a successful sign-in where the
  // backend cookie / token is set, the user object is in context,
  // but react-router's internal state may still have a stale
  // session_id hash. Without this, App.js could keep rendering
  // AuthCallback after a successful login (the user reported being
  // stuck on "Signing you in… One moment" indefinitely on prod).
  const liveHash = typeof window !== "undefined" ? window.location.hash : "";
  const hasSessionInHash =
    (location.hash || "").includes("session_id=") &&
    liveHash.includes("session_id=");
  if (hasSessionInHash) {
    return (
      <Layout>
        <AuthCallback />
      </Layout>
    );
  }
  return (
    <Layout>
      <Suspense fallback={<RouteLoading />}>
        <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/foryou" element={<ForYou />} />
        <Route path="/bible" element={<BiblePage />} />
        <Route path="/bible/:book/:chapter" element={<Reader />} />
        <Route path="/apocrypha" element={<Apocrypha />} />
        <Route path="/apocrypha/:book/:chapter" element={<ApocryphaReader />} />
        <Route path="/dictionary" element={<Dictionary />} />
        <Route path="/concordance" element={<Concordance />} />
        <Route path="/crossword" element={<Crossword />} />
        <Route path="/puzzles" element={<Crossword />} />
        <Route path="/precepts" element={<Precepts />} />
        <Route path="/sermons" element={<Sermons />} />
        <Route path="/news" element={<News />} />
        <Route path="/lessons" element={<Lessons />} />
        <Route path="/choir" element={<Choir />} />
        <Route path="/gospel-music" element={<GospelMusic />} />
        <Route path="/music-videos" element={<MusicVideos />} />
        <Route path="/comedy" element={<Comedy />} />
        <Route path="/comedy/foundational" element={<FoundationalComedy />} />
        <Route path="/logo-preview" element={<LogoPreview />} />
        <Route path="/christian-rap" element={<ChristianRap />} />
        <Route path="/demo" element={<Demo />} />
        <Route path="/demo/reel" element={<DemoReel />} />
        <Route path="/playlists" element={<Playlists />} />
        <Route path="/playlists/:slug" element={<Playlists />} />
        <Route path="/my-mix" element={<MyMix />} />
        <Route path="/community-shorts" element={<CommunityShorts />} />
        <Route path="/community-shorts/upload" element={<UploadShort />} />
        <Route path="/admin/analytics" element={<AdminAnalytics />} />
        <Route path="/videos" element={<Videos />} />
        <Route path="/kids" element={<Kids />} />
        <Route path="/movies" element={<Movies />} />
        <Route path="/movies/black-american" element={<MoviesBlackAmerican />} />
        <Route path="/memorial" element={<Memorial />} />
        <Route path="/churches" element={<Churches />} />
        <Route path="/admin/churches" element={<AdminChurches />} />
        <Route path="/admin/cloudinary" element={<AdminCloudinary />} />
        <Route path="/admin/voice-misses" element={<AdminVoiceMisses />} />
        <Route path="/admin/music" element={<AdminMusic />} />
        <Route path="/admin/review" element={<AdminReviewQueue />} />
        <Route path="/storybook" element={<Storybook />} />
        <Route path="/storybook/:storyId" element={<Storybook />} />
        <Route path="/discover" element={<Discover />} />
        <Route path="/shorts" element={<DiscoverShorts />} />
        <Route path="/wall" element={<Wall />} />
        <Route path="/live/:userId" element={<LiveViewer />} />
        <Route path="/live" element={<LivePage />} />
        <Route path="/prayer-wall" element={<PrayerWall />} />
        <Route path="/study-group" element={<StudyGroup />} />
        <Route path="/study-group/live" element={<StudyRoomLive />} />
        <Route path="/study-group/chat" element={<StudyChatEntry />} />
        <Route path="/rooms" element={<Rooms />} />
        <Route path="/rooms/:id" element={<Room />} />
        <Route path="/invite/:token" element={<InviteAccept />} />
        <Route path="/read" element={<Read />} />
        <Route path="/search" element={<Search />} />
        <Route path="/bookmarks" element={<Bookmarks />} />
        <Route path="/history" element={<History />} />
        <Route path="/my-notes" element={<MyNotes />} />
        <Route path="/congregation" element={<CreatorFeed />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/help/cast" element={<CastGuide />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/child-safety" element={<ChildSafety />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/sms-terms" element={<SmsTerms />} />
        <Route path="/acceptable-use" element={<AcceptableUse />} />
        <Route path="/aup" element={<AcceptableUse />} />
        <Route path="/about" element={<About />} />
        <Route path="/articles" element={<Articles />} />
        <Route path="/articles/:slug" element={<ArticleDetail />} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/tour" element={<Tour />} />
        <Route path="/my-prayers" element={<MyPrayers />} />
        <Route path="/sunday-school" element={<SundaySchool />} />
        <Route path="/recipes" element={<Recipes />} />
        <Route path="/recipes/:id" element={<RecipeDetail />} />
        <Route path="/profile" element={<MyProfile />} />
        <Route path="/studio" element={<CreatorStudio />} />
        <Route path="/u/:userId" element={<UserProfile />} />
        <Route path="/u/:userId/watch" element={<ChannelWatch />} />
        <Route path="/c/:slug" element={<ChannelBySlug />} />
        <Route path="/:handle" element={<ChannelBySlug />} />
        <Route path="/friends" element={<Friends />} />
        <Route path="/feed" element={<Navigate to="/foryou" replace />} />
        <Route path="/circles" element={<Circles />} />
        <Route path="/circles/:circleId" element={<CircleView />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/messages/:conversationId" element={<MessageThread />} />
        <Route path="/support" element={<Support />} />
        <Route path="/invite" element={<InviteFriends />} />
        <Route path="/donate" element={<Support />} />
        <Route path="/diagnostics" element={<Diagnostics />} />
        <Route path="/subscriptions" element={<Navigate to="/foryou" replace />} />
        <Route path="/my-lineup" element={<MyLineup />} />
        <Route path="/lineup" element={<MyLineup />} />
        <Route path="/subscribers" element={<SubscribersWatch />} />
        <Route path="/try" element={<DemoLogin />} />
        <Route path="/me" element={<Navigate to="/profile" replace />} />
        <Route path="/prayers" element={<Navigate to="/prayer-wall" replace />} />
        {/* HolyLetters (Scrabble-style Bible word game) — the page file
            existed but wasn't wired. Added Feb 2026 for Google Play
            closed-testing review. Also expose under /scramble alias
            since some marketing copy refers to it by that name. */}
        <Route path="/holy-letters" element={<HolyLetters />} />
        {/* /scramble — URL redirect alias so the address bar shows
            the canonical /holy-letters path. Was previously a direct
            <HolyLetters/> mount which loaded the component but kept
            the user on /scramble; testing-agent iteration_60 flagged
            this as a spec mismatch (functional but URL not updated). */}
        <Route path="/scramble" element={<Navigate to="/holy-letters" replace />} />
        {/* Friendly aliases for URLs that appear in marketing copy and
            external test instructions but differ from the implementation. */}
        <Route path="/signup" element={<Navigate to="/register" replace />} />
        <Route path="/paywall" element={<Navigate to="/support" replace />} />
        <Route path="/faith-based-rap" element={<Navigate to="/christian-rap" replace />} />
        <Route path="/pray-with-me" element={<Navigate to="/my-prayers" replace />} />
        <Route path="/companion" element={<Navigate to="/my-prayers" replace />} />
        {/* SEO: singular/legacy URLs Google flagged as Soft 404 → send them
            to the real (plural) pages so link equity + users land correctly. */}
        <Route path="/sermon" element={<Navigate to="/sermons" replace />} />
        <Route path="/lesson" element={<Navigate to="/lessons" replace />} />
        <Route path="/recipe" element={<Navigate to="/recipes" replace />} />
        <Route path="/recipe/*" element={<Navigate to="/recipes" replace />} />
        <Route path="/stories" element={<Navigate to="/storybook" replace />} />
        <Route path="/stories/*" element={<Navigate to="/storybook" replace />} />
        <Route path="/pricing" element={<Navigate to="/support" replace />} />
        <Route path="/plans" element={<Navigate to="/support" replace />} />
        {/* Graceful 404 for any deep-link that doesn't match. Previously
            this silently rewrote to "/", which masked broken links and
            made Google Play deep-link review impossible to fail-test. */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
      {!immersive && (
        <>
          <VoiceCommander />
          <MiniPlayer />
          <LineupCountBar />
          <CallOverlay />
          <QuickShortcutsRail />
          <LiveCaptions />
          <PushOptInBanner />
          <AddToHomeTip />
        </>
      )}
    </Layout>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <PaywallProvider>
            <AudioProvider>
              <I18nLanguageSync />
              <PrefsSync />
              <ScreenAwake />
              <AdSenseScript />
              <BackgroundMusicProvider>
                <MiniPlayerProvider>
                  <CaptionsProvider>
                    <LiveCallsProvider>
                      <WorshipRadioProvider>
                      <GospelRadioProvider>
                      <BrowserRouter>
                        <VoiceRoomProvider>
                          <AppRouter />
                        </VoiceRoomProvider>
                      </BrowserRouter>
                      <WorshipRadioBar />
                      <GospelRadioBar />
                      <GospelRadioSheet />
                      <InstallPrompt />
                      <Toaster
                        position="top-right"
                        richColors
                        theme="light"
                        closeButton
                        toastOptions={{
                          classNames: {
                            toast: "rounded-2xl border shadow-lg",
                            title: "font-medium",
                          },
                        }}
                      />
                      </GospelRadioProvider>
                      </WorshipRadioProvider>
                    </LiveCallsProvider>
                  </CaptionsProvider>
                </MiniPlayerProvider>
              </BackgroundMusicProvider>
            </AudioProvider>
          </PaywallProvider>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
